<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-comment" class="q-mb-sm" /> {{ $t('messages') }}
		</div>

		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="256">
					<q-input
						v-model="title"
						ref="title"
						:float-label="$t('title')"
						:error="$v.title.$error"
						@input="$v.title.$touch"
						:disable="disableFields"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('message') }}</p>

				<q-editor
					v-model="body"
					ref="body"
					:error="$v.body.$error"
					@input="$v.body.$touch"
					:disable="disableFields"
					:toolbar="[
						['bold', 'italic', 'strike', 'underline'],
						['left', 'center', 'right', 'justify'],
						['link', 'upload'],
						[
							{
								label: null,
								icon: 'fas fa-font',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
							},
							{
								label: null,
								icon: 'fas fa-text-height',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: [
									'size-1',
									'size-2',
									'size-3',
									'size-4',
									'size-5',
									'size-6',
									'size-7',
								],
							},
							'removeFormat',
						],
						['quote', 'unordered', 'ordered'],
						['custom_btn'],
					]"
					:definitions="{
						bold: { label: null, icon: 'fas fa-bold' },
						italic: { label: null, icon: 'fas fa-italic' },
						strike: { label: null, icon: 'fas fa-strikethrough' },
						underline: { label: null, icon: 'fas fa-underline' },
						left: { label: null, icon: 'fas fa-align-left' },
						center: { label: null, icon: 'fas fa-align-center' },
						right: { label: null, icon: 'fas fa-align-right' },
						justify: { label: null, icon: 'fas fa-align-justify' },
						link: { label: null, icon: 'fas fa-link' },
						upload: { tip: null, icon: 'fas fa-file-image', handler: insertImg },
						removeFormat: { label: null, icon: 'fas fa-eraser' },
						quote: { label: null, icon: 'fas fa-quote-right' },
						unordered: { label: null, icon: 'fas fa-list-ul' },
						ordered: { label: null, icon: 'fas fa-list-ol' },
					}"
				>
					<q-btn
						slot="custom_btn"
						dense
						flat
						color="black"
						icon="fas fa-code"
						label="HTML"
						@click="show_html = true"
					/>
				</q-editor>
			</div>
		</div>
		<div class="col-xs-12 col-sm-12">
			<q-checkbox
				v-model="is_schedule_message"
				class="q-mb-md"
				:label="$t('schedule_message')"
				:disable="disableFields"
			/>
		</div>
		<div v-if="is_schedule_message" class="row gutter-sm q-mb-md">
			<div class="col-xs-12 col-sm-6">
				<q-select
					v-model="timezone"
					ref="timezone"
					:options="timezones"
					:float-label="$t('timezone')"
					filter
					:error="$v.timezone.$error"
					@input="$v.timezone.$touch"
					:disable="disableFields"
				/>
			</div>
			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="schedule_date"
					ref="schedule_date"
					type="datetime"
					:float-label="$t('publish_date')"
					format="DD/MM/YYYY HH:mm"
					:error="$v.schedule_date.$error"
					@input="$v.schedule_date.$touch"
					:disable="disableFields"
				/>
			</div>
		</div>
		<q-tabs v-model="tab" color="secondary" class="shadow-1">
			<q-tab
				slot="title"
				name="permissions"
				icon="fas fa-clipboard-check"
				:label="$t('areas_and_profiles')"
			/>

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox
							class="q-mb-sm"
							:disable="disableFields"
							v-model="all_areas"
							:val="true"
							:label="$t('select_all_areas')"
							@input="checkAllAreas()"
						/>
						<select-multiple
							label="knowledge_areas"
							:filters="area"
							:options="areas"
							@on_value="area = $event"
							:error="$v.area.$error"
							:input="$v.area.$touch"
							:disable="disableFields"
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAll()"
									:disable="disableFields"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in profiles"
								:key="index"
								:class="getErrorClass($v.profile.$error)"
							>
								<q-checkbox
									keep-color
									v-model="profile"
									ref="profile"
									:val="item.value"
									:label="item.label"
									@input="$v.profile.$touch"
									:color="getErrorColor($v.profile.$error)"
									:disable="disableFields"
								/>
							</div>

							<div class="col-xs-12 col-sm-12">
								<q-select
									v-model="user_group"
									ref="user_group"
									:options="user_groups"
									:float-label="$t('groups')"
									:error="$v.user_group.$error"
									@input="$v.user_group.$touch"
									multiple
									chips
									:disable="disableFields"
								/>
							</div>
						</div>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<q-table
			class="q-mt-md"
			:data="users"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="getUserList"
			:loading="loading_users"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					icon="fas fa-search"
					:placeholder="$t('search')"
					:debounce="600"
				/>
				<div class="col" />
				<div class="q-subheading">{{ $t('recipients') }} ({{ total_recipients }})</div>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props">
					{{ props.row.id }}
				</q-td>

				<q-td key="full_name" :props="props">
					{{ props.row.full_name }}
				</q-td>

				<q-td key="email" :props="props">
					{{ props.row.email }}
				</q-td>

				<q-td key="profile" :props="props">
					{{ props.row.profile }}
				</q-td>

				<q-td key="opened_date" :props="props">
					{{ openedDateHandler(props.row.opened_date) }}
				</q-td>
			</q-tr>
		</q-table>

		<div class="row gutter-sm q-mt-sm">
			<div>
				<p class="caption">{{ $t('send_email') }}</p>
				<q-btn-toggle
					v-model="send_email"
					:toggle-color="send_email ? 'positive' : 'negative'"
					:options="[
						{ value: true, label: $t('yes') },
						{ value: false, label: $t('no') },
					]"
					:disable="disableFields"
				/>
			</div>
			<div v-if="send_email">
				<p class="caption">{{ $t('send_body_email') }}</p>
				<q-btn-toggle
					v-model="send_body_email"
					:toggle-color="send_body_email ? 'positive' : 'negative'"
					:options="[
						{ value: true, label: $t('yes') },
						{ value: false, label: $t('no') },
					]"
					:disable="disableFields"
				/>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="fas fa-chevron-left"
					@click="$router.push({ name: 'admin_messages' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
					:disable="disableFields"
				/>
			</div>
		</div>

		<q-modal
			v-model="show_html"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_html = false" icon="fas fa-times" />
					<q-toolbar-title>
						HTML
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row">
						<div class="col-12">
							<q-input type="textarea" rows="10" float-label="HTML" v-model="body" />
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('close')"
								icon="fas fa-ban"
								@click="show_html = false"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import 'moment-timezone'
import { BlobServiceClient } from '@azure/storage-blob'
import SelectMultiple from 'components/utils/SelectMultiple'
export default {
	name: 'AdminMessage',

	i18nOptions: { namespaces: ['custom', 'common', 'messages', 'admin'] },

	data() {
		return {
			id: '',
			title: '',
			body: '',
			send_email: false,
			is_schedule_message: false,
			timezone: null,
			schedule_date: null,
			send_body_email: false,
			status: 1,
			send_date: null,
			area: [],
			selected_areas: [],
			profile: [],
			user_group: [],
			all_profiles: false,
			tab: 'permissions',
			area_loaded: false,
			user_group_loaded: false,
			profile_loaded: false,
			show_html: false,
			all_areas: false,
			// Q-editor Image to be uploaded
			upload_url: process.env.API_URL + '/files/policy/',
			urls: [],
			cancel_token: '',
			source: '',
			crop_modal: false,
			original_image: '',
			fileUrl: '',
			fileUrlSigned: '',
			// Recipients table
			total_recipients: 0,
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 10,
				descending: false,
				sortBy: null,
			},
			loading_users: false,
			users: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'id',
					sortable: false,
				},
				{
					name: 'full_name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'full_name',
					sortable: false,
				},
				{
					name: 'email',
					required: true,
					label: 'email',
					align: 'left',
					field: 'email',
					sortable: false,
				},
				{
					name: 'profile',
					required: true,
					label: 'profile',
					align: 'left',
					field: 'profile',
					sortable: false,
				},
				{
					name: 'opened_date',
					required: true,
					label: 'view_date',
					align: 'left',
					field: 'opened_date',
					sortable: false,
				},
			],
			filter: '',
		}
	},

	components: {
		'select-multiple': SelectMultiple,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			faq_categories: 'registration_lists/faq_categories',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			id_user_language_selected: 'common/id_user_language_selected',
			num_rows: 'datatable/num_rows',
			timezones: 'registration_lists/timezones',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		disableFields() {
			if (this.id || this.status === 2 || this.status === 3) {
				return true
			} else return false
		},

		getMaxWidth() {
			return this.$q.platform.is.mobile ? '100vw' : '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},
	},

	methods: {
		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let fd = new FormData()
				fd.append('title', this.title)
				fd.append('body', this.$clearEmoticons(this.body))
				fd.append('send_email', this.send_email)
				fd.append('send_body_email', this.send_body_email)

				if (this.send_date) fd.append('send_date', this.send_date)

				if (this.area.length > 0 && this.profile.length > 0) {
					this.area.forEach((area) => {
						fd.append('areas', area)
					})

					this.profile.forEach((profile) => {
						fd.append('profiles', profile)
					})
				}

				if (this.user_group.length > 0) {
					this.user_group.forEach((user_group) => {
						fd.append('user_groups', user_group)
					})
				}

				if (this.is_schedule_message) {
					fd.append('timezone', this.timezone)
					fd.append(
						'schedule_date',
						moment(
							date.formatDate(this.schedule_date, 'YYYY-MM-DDTHH:mm')
						)
							.tz(this.timezone, true)
							.format('YYYY-MM-DDTHH:mm:ssZ')
					)
				}

				if (this.id != 0) {
					this.$axios
						.put(`/admin_messages/messages/${this.id}/`, fd, this.getHeader)
						.then(() => {
							this.$q.loading.hide()

							this.$q.notify({
								message: this.$t('message_edit_success'),
								type: 'positive',
								icon: 'far fa-save',
							})

							this.$router.push({ name: 'admin_messages' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else {
					this.$axios
						.post(`/admin_messages/messages/`, fd, this.getHeader)
						.then((res) => {
							this.$q.loading.hide()
							this.$q.notify({
								message: this.$t('message_add_success'),
								type: 'positive',
								icon: 'far fa-save',
							})

							this.$router.push({ name: 'admin_messages' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_messages/messages/${this.id}/`, this.getHeader)
				.then((response) => {
					this.title = response.data.title
					this.body = response.data.body
					this.send_date = response.data.send_date
					this.send_email = response.data.send_email
					this.send_body_email = response.data.send_body_email

					this.profile = response.data.profiles
					this.area = response.data.areas
					this.user_group = response.data.user_groups
					this.status = response.data.status
					this.total_recipients = response.data.qty_users_sent

					this.status !== 0
						? this.getUserList({ pagination: this.server_pagination })
						: null

					this.timezone = response.data.timezone
					this.schedule_date = response.data.schedule_date
					this.is_schedule_message = Boolean(this.timezone || this.schedule_date)
					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		insertImg() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.png, .jpg, .gif'
			let file

			input.onchange = (_) => {
				const files = Array.from(input.files)
				file = files[0]

				this.uploadFile(file)
			}
			input.click()
		},

		uploadFile(file) {
			let date = moment()

			let fd = new FormData()
			fd.append('filename', file.name)
			fd.append(
				'file_directory',
				`media_public/html_images/messages/${date.year()}/${date.month() + 1}/`
			)

			this.$axios
				.post('/api/files/policy/', fd, this.getHeader, { cancelToken: this.source.token })
				.then((response) => {
					return new Promise((resolve) => {
						this.$q.loading.show()

						if (response.data.origin === 'azure') {
							const blobServiceClient = new BlobServiceClient(
								`${response.data.base_url}?${response.data.sas_token}`
							)

							const main = async () => {
								const container_client = blobServiceClient.getContainerClient('')

								const block_blob_client = container_client.getBlockBlobClient(
									response.data.filename
								)
								const upload_blob_response = await block_blob_client.uploadBrowserData(
									file
								)

								let fd_complete = new FormData()
								fd_complete.append('uploaded', true)
								fd_complete.append('fileSize', file.size)
								fd_complete.append('file', response.data.file_id)

								if (this.custom_directory) {
									fd_complete.append('not_signed', true)
									fd_complete.append('bucket_name', 'static')
								}

								this.$axios
									.post('/api/files/complete/', fd_complete, this.getHeader)
									.then((response) => {
										this.fileUrl = response.data.fileUrl
										this.fileUrlSigned = response.data.fileUrlSigned
										document.execCommand(
											'insertHTML',
											true,
											'<div><img class="uploadBase" src="' +
												this.fileUrl +
												'" style="max-width: 100%" /></div>'
										)
									})
									.finally(() => {
										resolve()
										this.$q.loading.hide()
									})
							}

							main()
						} else {
							let content_type =
								file.type != '' ? file.type : 'application/octet-stream'
							let policy = response.data
							let file_name = policy.filename
							let key_path = policy.file_bucket_path

							let fd_s3 = new FormData()
							fd_s3.append('key', key_path + file_name)
							fd_s3.append('acl', 'private')
							fd_s3.append('Content-Type', content_type)
							fd_s3.append('AWSAccessKeyId', policy.key)
							fd_s3.append('Policy', policy.policy)
							fd_s3.append('filename', file_name)
							fd_s3.append('Signature', policy.signature)
							fd_s3.append('file', file)

							this.cancel_token = this.$axios.CancelToken
							this.source = this.cancel_token.source()

							this.$axios
								.post(process.env.BUCKET_URL, fd_s3, {
									// onUploadProgress: function( event ) {
									//     updateProgress(event.loaded / event.total)
									// },
									// cancelToken: this.source.token
								})
								.then(() => {
									let fd_complete = new FormData()
									fd_complete.append('uploaded', true)
									fd_complete.append('fileSize', file.size)
									fd_complete.append('file', policy.file_id)

									this.$axios
										.post('/api/files/complete/', fd_complete, this.getHeader)
										.then((response) => {
											this.fileUrl = response.data.fileUrlSigned.split('?')[0]

											document.execCommand(
												'insertHTML',
												true,
												`<div><img class="uploadBase" src="${
													this.fileUrl
												}" style="max-width: 100%" /></div>`
											)
										})
										.finally(() => {
											this.$q.loading.hide()
										})
								})
								.finally(() => {
									resolve()
								})
						}
					})
				})
				.catch((e) => {
					console.log(e)
					this.$q.loading.hide()
				})
		},

		getOrder(name) {
			if (name == 'cancel') {
				return this.$q.platform.is.mobile ? 'order-last' : 'order-first'
			} else if (name == 'save') {
				return this.$q.platform.is.mobile ? 'order-first' : 'order-last'
			}
		},

		getErrorColor(state) {
			return state ? 'negative' : ''
		},

		getErrorClass(state) {
			return state ? 'checkbox-error' : ''
		},

		getUserList({ pagination }) {
			this.loading_users = true
			this.server_pagination = pagination

			const order = pagination.descending ? '-' : ''
			const order_by = pagination.sortBy ? order + pagination.sortBy : ''

			const data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				area: this.area,
				user_group: this.user_group,
				profile: this.profile,
				search: this.filter,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get(
					`/admin_messages/messages/${this.id}/get_sent_users/?${this.$qs.stringify(
						data,
						{ arrayFormat: 'repeat' }
					)}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count
					this.users = response.data.results
						? response.data.results.map((user) => user)
						: []
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => (this.loading_users = false))
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles.forEach((element) => {
					this.profile.push(element.value)
				})
			} else {
				this.profile = []
			}
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.area = this.areas.map((e) => e.value)
				this.$v.area.$touch
			} else {
				this.area = []
			}
		},

		fDate(d) {
			if (d) {
				let format = ''

				switch (this.id_user_language_selected) {
					case 1:
					case 3:
						format = 'DD/MM/YYYY HH:mm:ss'
						break
					default:
						format = 'MM-DD-YYYY HH:mm:ss'
						break
				}

				return date.formatDate(d, format)
			}
		},

		openedDateHandler(d) {
			return d !== null ? this.fDate(d) : this.$t('not_viewed_yet')
		},

		fetchTimezones() {
			let values = []
			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})
					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')
			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},
	},

	created() {
		this.fetchTimezones()
		this.id = this.$route.params.id

		if (this.id != 0) {
			this.loadPagination()
			this.fetchData()
		} else {
			this.area_loaded = true
			this.user_group_loaded = true
			this.profile_loaded = true
		}
	},

	watch: {
		$route: function(refreshPage) {
			this.id = this.$route.params.id

			if (this.id != 0) this.fetchData()
			else {
				this.area_loaded = true
				this.user_group_loaded = true
				this.profile_loaded = true
			}
		},

		area: {
			handler: function(val, oldVal) {
				let item = this.areas.filter((area, idx) => val[idx] === area.value)

				this.selected_areas = item.map((area) => area.area_id)

				if (this.area_loaded) {
					this.getUserList({ pagination: this.server_pagination })
				} else {
					this.area_loaded = true
				}
			},
			deep: true,
		},

		profile: {
			handler: function(val, oldVal) {
				if (this.profile_loaded) {
					this.getUserList({ pagination: this.server_pagination })
				} else {
					this.profile_loaded = true
				}
			},
			deep: true,
		},

		user_group: {
			handler: function(val, oldVal) {
				if (this.user_group_loaded) {
					this.getUserList({ pagination: this.server_pagination })
				} else {
					this.user_group_loaded = true
				}
			},
			deep: true,
		},
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			body: { required },
			area: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			profile: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			user_group: {
				requiredIf: requiredIf(() => {
					if (
						this.profile.length == 0 &&
						this.area.length == 0 &&
						this.user_group.length == 0
					) {
						return true
					} else {
						return false
					}
				}),
			},
			timezone: {
				requiredIf: requiredIf(() => this.is_schedule_message),
			},
			schedule_date: {
				requiredIf: requiredIf(() => this.is_schedule_message),
			},
		}
	},
}
</script>

<style></style>
